import { GuestCountError, parseGuestCount } from "./workbook";

export class GuestInputError extends Error {}

export const GUEST_MENUS = [
  { value: "comfort", label: "Μενού Α", short: "Α" },
  { value: "social", label: "Μενού Β", short: "Β" },
  { value: "nagia", label: "Μενού Γ", short: "Γ" },
] as const;

export type GuestMenu = (typeof GUEST_MENUS)[number]["value"];

export type GuestSeat = {
  name: string;
  table: string;
};

export type GuestFinalization = {
  guest_count: number;
  menu: string;
  venue_name: string;
};

const TABLE_MESSAGE = "Ο αριθμός τραπεζιού είναι από 1 έως 60.";

export function guestMenuLabel(value: string | null | undefined): string {
  const known = GUEST_MENUS.find((menu) => menu.value === value);
  return known?.label ?? (value ?? "").trim();
}

export function parseGuestMenu(value: unknown): string {
  if (typeof value !== "string") throw new GuestInputError("Διάλεξε το μενού.");
  const text = value.trim();
  if (!text) throw new GuestInputError("Διάλεξε το μενού.");
  const known = GUEST_MENUS.find((menu) => menu.value === text || menu.label === text || menu.short === text);
  if (known) return known.value;
  if (text.length > 120) throw new GuestInputError("Το μενού είναι πολύ μεγάλο.");
  return text;
}

export function parseGuestHeadcount(value: unknown): number {
  if (value == null || value === "" || (typeof value === "string" && value.trim() === "")) {
    throw new GuestInputError("Γράψε τον αριθμό των καλεσμένων.");
  }
  try {
    const count = parseGuestCount(value, 1);
    if (count == null) throw new GuestInputError("Γράψε τον αριθμό των καλεσμένων.");
    return count;
  } catch (error) {
    if (error instanceof GuestInputError) throw error;
    if (error instanceof GuestCountError) throw new GuestInputError("Οι καλεσμένοι γράφονται ως ακέραιος αριθμός.");
    throw error;
  }
}

export function parseGuestFinalization(value: unknown): GuestFinalization {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new GuestInputError("Η οριστικοποίηση δεν είναι έγκυρη.");
  const record = value as Record<string, unknown>;
  return {
    guest_count: parseGuestHeadcount(record.guest_count),
    menu: parseGuestMenu(record.menu),
    venue_name: parseVenue(record.venue_name),
  };
}

export function parseGuestSeats(value: unknown): GuestSeat[] {
  if (!Array.isArray(value)) throw new GuestInputError("Η λίστα καλεσμένων δεν είναι έγκυρη.");
  if (value.length > 1000) throw new GuestInputError("Η λίστα έχει πάρα πολλές γραμμές.");
  const seats: GuestSeat[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object" || Array.isArray(item)) throw new GuestInputError("Μια γραμμή καλεσμένου δεν είναι έγκυρη.");
    const record = item as Record<string, unknown>;
    const name = typeof record.name === "string" ? record.name.trim() : "";
    const tableEmpty = record.table == null || (typeof record.table === "string" && record.table.trim() === "");
    if (!name && tableEmpty) continue;
    if (!name) throw new GuestInputError("Γράψε όνομα δίπλα σε κάθε τραπέζι.");
    if (name.length > 120) throw new GuestInputError("Ένα όνομα είναι πολύ μεγάλο.");
    if (tableEmpty) throw new GuestInputError("Γράψε αριθμό τραπεζιού δίπλα σε κάθε όνομα.");
    seats.push({ name, table: parseSeatTable(record.table) });
  }
  if (!seats.length) throw new GuestInputError("Γράψε τουλάχιστον έναν καλεσμένο και το τραπέζι του.");
  return seats;
}

export function guestListMessage(venue: string, link: string): { subject: string; text: string; html: string } {
  const house = venue.trim() || "τον χώρο";
  const subject = `Λίστα καλεσμένων — ${house}`;
  const text = [
    "Καλησπέρα,",
    "",
    `Σας στέλνουμε τη φόρμα για την οριστικοποίηση των καλεσμένων στο ${house}.`,
    "Γράψτε το όνομα κάθε καλεσμένου και δίπλα τον αριθμό του τραπεζιού:",
    link,
    "",
    "Με εκτίμηση,",
    house,
  ].join("\n");
  const safe = escapeHtml(link);
  const html = [
    "<p>Καλησπέρα,</p>",
    `<p>Σας στέλνουμε τη φόρμα για την οριστικοποίηση των καλεσμένων στο ${escapeHtml(house)}.</p>`,
    `<p><a href="${safe}">Ονόματα καλεσμένων και τραπέζια</a></p>`,
    `<p>Με εκτίμηση,<br>${escapeHtml(house)}</p>`,
  ].join("");
  return { subject, text, html };
}

function parseSeatTable(value: unknown): string {
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 1 || value > 60) throw new GuestInputError(TABLE_MESSAGE);
    return String(value);
  }
  if (typeof value !== "string") throw new GuestInputError(TABLE_MESSAGE);
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) throw new GuestInputError(TABLE_MESSAGE);
  const parsed = Number(trimmed);
  if (parsed < 1 || parsed > 60) throw new GuestInputError(TABLE_MESSAGE);
  return String(parsed);
}

function parseVenue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value !== "string") throw new GuestInputError("Ο χώρος δεν είναι κείμενο.");
  const text = value.trim();
  if (text.length > 120) throw new GuestInputError("Ο χώρος είναι πολύ μεγάλος.");
  return text;
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
